import { Maybe } from '@graphql-tools/utils'
import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { Seo } from '@/layout/Seo'
import { DefaultLayout } from '@/layouts/DefaultLayout'
import { NextPageWithLayout } from '@/layouts/types'
import {
  getCollection,
  GUILD_MEMBERSHIP_NUMBERS_ID,
  UNIQUE_SHARE_CLAIM_USERS_ID,
} from '@/services/CmsService/Collection'
import { Collection } from '@/types/codegen-contentful'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { getGenericStaticPaths } from '@/utils/nextUtils/staticPages'
import { omitUndefineds } from '@/utils/object'
import { getBasePath } from '@/utils/sitemap/base'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { GuildImpactView } from '@/views/GuildImpactView'

interface GuildImpactPageProps {
  locale: string
  preview: boolean
  guildMembershipNumbers: Collection
  numberOfUniqueShareClaimUsers: Maybe<string>
}

const GuildImpactPage: NextPageWithLayout<GuildImpactPageProps> = ({
  locale,
  guildMembershipNumbers,
  numberOfUniqueShareClaimUsers,
}) => {
  const { t } = useTranslate('guild')
  const basePath = `${getBasePath(locale)}`
  const url = `${basePath}${paths.guild.impact}`
  const title = t('angelGuildImpactTitle', 'Angel Guild Impact | Angel')
  const description = t(
    'angelStudiosGuildImpactMetaDescriptionV3',
    'Guild members voted and gave feedback to bring these films to life. See how these stories are inspiring real people.',
  )
  return (
    <>
      <Seo
        canonical={url}
        description={description}
        openGraph={{ title, description, url }}
        path={paths.guild.impact}
        title={title}
      />
      <GuildImpactView
        guildMembershipNumbers={guildMembershipNumbers}
        numberOfUniqueShareClaimUsers={numberOfUniqueShareClaimUsers}
      />
    </>
  )
}

GuildImpactPage.getLayout = (page) => {
  return <DefaultLayout>{page}</DefaultLayout>
}

export const getStaticPaths = getGenericStaticPaths

export const getStaticProps = getStaticPropsErrorHandler<GuildImpactPageProps>(
  { filePath: 'pages/[locale]/guild/impact/index.tsx' },
  async ({ locale, preview, next404 }) => {
    const [rawGuildMembershipNumbers, uniqueShareClaimUsersCollection, translations] = await Promise.all([
      getCollection({ locale, preview }, GUILD_MEMBERSHIP_NUMBERS_ID),
      getCollection({ locale, preview }, UNIQUE_SHARE_CLAIM_USERS_ID),
      await loadTranslations(locale, ['common', 'guild', 'watch']),
    ])

    const guildMembershipNumbers = omitUndefineds(rawGuildMembershipNumbers) ?? null
    const numberOfUniqueShareClaimUsers = uniqueShareClaimUsersCollection?.customData?.uniqueShareClaimUsers ?? null

    if (!guildMembershipNumbers) {
      return next404()
    }

    return {
      props: {
        locale,
        preview,
        guildMembershipNumbers,
        numberOfUniqueShareClaimUsers,
        ...translations,
      },
      revalidate: Durations.FIFTEEN_MINUTES_IN_SECONDS,
    }
  },
)

export default GuildImpactPage
